import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Spacer } from "@amzn/stencil-react-components/layout";
import { ModalContent } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { IItemVersion } from "lib/types/items";
import React, { FunctionComponent, useEffect, useState } from "react";

import { getString } from "../../uistringlabels/uiStringUtils";
import { SelectChildSku, VersionObject } from "../SelectChildSku";

/**
 * Props interface for the TeamAccessRequestModal component
 * Defines all properties needed for the team access request modal functionality
 */
export interface TeamAccessRequestModalProps {
  /**
   * Whether the modal is currently open and visible
   * Controls the modal's visibility state
   */
  isOpen: boolean;

  /**
   * Callback function to close the modal
   * Called when user cancels or completes the team selection process
   */
  onClose: () => void;

  /**
   * Callback function to handle the order request for the selected team
   * Called when user confirms their team selection and submits the request
   *
   * @param selectedTeam - The team/version object selected by the user
   */
  onOrderTeam: (selectedTeam: VersionObject) => void;

  /**
   * Available teams/versions to select from in the dropdown
   * Array of item versions representing different teams that can be requested
   */
  availableTeams: IItemVersion[];

  /**
   * Product name for display in modal title and description
   * Used to provide context about which product the team access is being requested for
   */
  productName: string;

  /**
   * Whether the order submission is currently being processed
   * Shows loading indicators and disables form interactions when true
   */
  isLoading?: boolean;
}

/**
 * TeamAccessRequestModal Component
 *
 * Modal component for requesting access to additional teams for parent-child products.
 * Allows users to select from available teams and submit access requests.
 *
 * Features:
 * - Team selection dropdown with available teams
 * - Form validation (requires team selection)
 * - Loading states during submission
 * - Proper modal lifecycle management
 * - Accessibility support with proper ARIA labels
 *
 * @param props - Component props as defined in TeamAccessRequestModalProps
 * @returns JSX element for the team access request modal or null if not open
 */
export const TeamAccessRequestModal: FunctionComponent<TeamAccessRequestModalProps> = ({
  isOpen,
  onClose,
  onOrderTeam,
  availableTeams,
  productName,
  isLoading = false,
}) => {
  const [selectedTeam, setSelectedTeam] = useState<VersionObject | undefined>();

  /**
   * Handles team selection change from the dropdown
   * Updates the local state with the selected team
   *
   * @param team - The team/version object selected by the user
   */
  const handleTeamChange = (team: VersionObject): void => {
    setSelectedTeam(team);
  };

  /**
   * Reset selected team when modal opens/closes
   */
  useEffect(() => {
    if (!isOpen) {
      setSelectedTeam(undefined);
    }
  }, [isOpen]);

  /**
   * Handles the order submission when user clicks the order button
   * Validates that a team is selected before calling the parent callback
   */
  const handleOrderClick = (): void => {
    if (selectedTeam) {
      onOrderTeam(selectedTeam);
    }
  };

  /**
   * Handles modal close event
   * Resets the selected team state and calls the parent close callback
   */
  const handleClose = (): void => {
    setSelectedTeam(undefined);
    onClose();
  };

  /**
   * Converts IItemVersion array to VersionObject array for SelectChildSku component
   * Maps team data to the format expected by the selection component
   */
  const teamVersions: VersionObject[] = availableTeams.map((team) => ({
    taxonomyId: team.taxonomyId,
    displayText: team.name || team.displayText || team.taxonomyId,
  }));

  if (!isOpen) return null;

  return (
    <ModalContent
      titleText={`${getString("teamAccess.modalTitle")} - ${productName}`}
      maxWidth={"815px"}
      buttons={[
        <Button
          key="team-access-cancel"
          variant={ButtonVariant.Secondary}
          onClick={handleClose}
          disabled={isLoading}
          data-testid="cancel-team-access-button"
        >
          {getString("common.cancel")}
        </Button>,
        <Button
          key="team-access-order"
          variant={ButtonVariant.Primary}
          onClick={handleOrderClick}
          disabled={!selectedTeam || isLoading}
          icon={isLoading ? <Spinner size={SpinnerSize.Small} /> : undefined}
          data-cy="order-team-button"
          data-testid="order-team-button"
        >
          {isLoading ? getString("ordering.validating") : getString("ordering.orderNow")}
        </Button>,
      ]}
    >
      <Col gridGap="S200" alignSelf="flex-start" className="team-access-modal-content">
        <Text fontSize="T200" color="neutral90">
          {getString("teamAccess.modalDescription", { productName: productName })}
        </Text>

        <Text fontSize="T100" color="neutral70" style={{ lineHeight: "1.5" }}>
          {getString("teamAccess.modalExplanation")}
        </Text>

        <Col gridGap="S100">
          <SelectChildSku
            itemChildVersions={teamVersions}
            onChange={handleTeamChange}
            selectedVersion={selectedTeam}
            autoSelectFirstVersion={false}
            isLegacy={false}
          />
        </Col>

        <Col
          gridGap="S100"
          style={{ marginTop: "16px", padding: "16px", backgroundColor: "#e6f2ff", borderRadius: "8px" }}
        >
          <Text fontSize="T200" fontWeight="medium" color="neutral90">
            {getString("teamAccess.freeAccessTitle", { productName: productName })}
          </Text>
          <Text fontSize="T100" color="neutral70">
            {getString("teamAccess.freeAccessNotice")}
          </Text>
        </Col>
      </Col>
    </ModalContent>
  );
};
